import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import './i18n';
import { App } from './App';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { CountryImagesProvider } from './context/CountryImagesContext';
import { ToastProvider } from './context/ToastContext';
import { queryClient } from './api/query-client';
import { initVisualViewport } from './utils/viewport';
import { initTelegramWebApp, loadTelegramScript, markTelegramSession, shouldLoadTelegramSdk } from './utils/platform';
import { captureReferralCode } from './utils/referral';
import './theme/tokens.css';
import './theme/components.css';
import './theme/desktop.css';
import './index.css';

// Heavy route/sheet CSS — load after first paint so it is not render-blocking on home.
function loadDeferredStyles() {
  void import('./theme/onboarding.css');
  void import('./theme/country-premium.css');
}

if (typeof window !== 'undefined') {
  const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof ric === 'function') {
    ric(() => loadDeferredStyles(), { timeout: 2000 });
  } else {
    window.setTimeout(loadDeferredStyles, 1);
  }
}

try {
  localStorage.removeItem('cs-country-landmarks-v2');
  localStorage.removeItem('cs-country-landmarks-v3');
} catch {
  /* private mode */
}

async function boot() {
  // Hash `tgWebAppStartParam` is on the URL before the SDK runs — persist first.
  captureReferralCode();

  if (shouldLoadTelegramSdk()) {
    // Stamp before SDK init — Telegram Web clears #tgWebApp* on refresh, so
    // the next reload still knows this tab is a Mini App.
    try {
      const hash = window.location.hash || '';
      if (hash.includes('tgWebApp') || /Telegram/i.test(navigator.userAgent || '')) {
        markTelegramSession();
      }
    } catch {
      /* ignore */
    }
    try {
      await loadTelegramScript();
      initTelegramWebApp();
    } catch {
      // Web fallback — app still works without Telegram SDK.
    }
  }

  // After SDK parse, initDataUnsafe.start_param is the canonical source.
  captureReferralCode();

  initVisualViewport();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <ToastProvider>
                <CountryImagesProvider>
                  <App />
                </CountryImagesProvider>
              </ToastProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void boot();
